import Tag from '@atoms/Tag'
import { scopedTranslation } from '@utils/I18n'
import type { GroupShowQueryType } from '@frontend/graphql/queries/groups'

import { DetailRow } from './GroupDetailsPanel'

const t = scopedTranslation('components')
const tShared = scopedTranslation('shared')

export type CriteriaType = GroupShowQueryType['group']['criteria']
type RulesType = CriteriaType['rules']['nodes']
type RuleComponentProps = { rule: RulesType[0]; tagSize?: 'xs' | 'sm' }
type GroupCriteriaProps = { criteria: CriteriaType }

const TagRule = ({ rule, tagSize = 'sm' }: RuleComponentProps) => {
  return (
    <p className="tw:grid tw:grid-cols-[max-content_minmax(0,1fr)] tw:items-baseline">
      <span className="tw:pr-1 tw:inline-flex">
        <strong className="tw:font-bold">{t(`group_criteria.properties.${rule.property}`)}:&nbsp;</strong>
        <span>{t(`group_criteria.predicates.${rule.predicate}`)}&nbsp;</span>
      </span>
      <span className="tw:inline-flex tw:items-center tw:flex-wrap tw:gap-2">
        {rule.values.map((tag) => (
          <Tag key={tag} size={tagSize} wrap>
            {tag}
          </Tag>
        ))}
      </span>
    </p>
  )
}

const GenericRule = ({ rule }: RuleComponentProps) => {
  return (
    <p>
      <strong className="tw:font-bold">{t(`group_criteria.properties.${rule.property}`)}: </strong>
      <span>{t(`group_criteria.predicates.${rule.predicate}`)}&nbsp;</span>
      {rule.values.join(', ')}
    </p>
  )
}

export const RuleComponent = ({ rule, tagSize }: RuleComponentProps) => {
  switch (rule.property) {
    case 'tags':
      return <TagRule rule={rule} tagSize={tagSize} />
    default:
      return <GenericRule rule={rule} />
  }
}

const GroupCriteria = ({ criteria }: GroupCriteriaProps) => {
  const {
    combinator,
    rules: { nodes: rules },
  } = criteria

  return (
    <DetailRow icon="funnel" label={t('group_criteria.criteria')} block testId="group-criteria">
      {rules.length === 0 ? (
        <p className="tw:m-0">
          <strong className="tw:font-bold">{tShared('labels.none')} </strong>
          <span>({t('group_criteria.all_supporters_included')})</span>
        </p>
      ) : (
        <div className="tw:flex tw:flex-col tw:gap-2">
          <p className="tw:m-0 tw:text-xs tw:text-gray-true-500">
            {t(`group_criteria.combinator_explanation.${combinator.toLowerCase()}`)}
          </p>
          {rules.map((rule) => (
            <RuleComponent key={rule.id} rule={rule} />
          ))}
        </div>
      )}
    </DetailRow>
  )
}

export default GroupCriteria
